import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import Layout from "../layouts/layout";
import Loader from "../components/loader";
import BILLING from "../api/services/BILLING";
import { paths } from "../routes/paths";
import PosInvoicing from "./documents/posInvoicing";
import DocumentsList from "./documents/documentsList";
import DocumentsDebts from "./documents/documentsDebts";
import DocumentsSettings from "./documents/documentsSettings";
import settingsCogLottie from "../assets/lotties/settings-cog.json";

const TABS = [
  { id: "pos", label: "קופה" },
  { id: "list", label: "מסמכים שהופקו" },
  { id: "debts", label: "חובות" },
  { id: "settings", label: "הגדרות", icon: "cog" },
];

// Native invoicing is only offered to businesses that have connected an external
// invoice provider (Morning). Until then the screen is not shown at
// all: issuing a document is a tax event, and a business that has not told us
// which provider it files through must not be handed an issuing UI it might use
// by accident. Gating here rather than in the sidebar keeps a bookmarked or
// deep-linked /documents closed too.
const EXTERNAL_PROVIDERS = ["greeninvoice"];

const Documents = () => {
  const [tab, setTab] = useState("pos");
  const cogRef = useRef(null);
  const navigate = useNavigate();

  const [providerLoading, setProviderLoading] = useState(true);
  const [provider, setProvider] = useState("");

  useEffect(() => {
    let alive = true;
    BILLING.get_payment_cred()
      .then((res) => {
        if (!alive) return;
        const d = res?.data?.data || res?.data || {};
        setProvider(d.invoiceProvider || "");
      })
      // Fail CLOSED: if we cannot confirm a provider is connected, do not open
      // an issuing screen. The cost of a wrong "yes" here is a real document.
      .catch(() => alive && setProvider(""))
      .finally(() => alive && setProviderLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const hasExternalProvider = EXTERNAL_PROVIDERS.includes(provider);

  if (providerLoading) {
    return (
      <Layout>
        <Loader />
      </Layout>
    );
  }

  if (!hasExternalProvider) {
    return (
      <Layout>
        <div
          style={{
            direction: "rtl", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", textAlign: "center",
            gap: 14, maxWidth: 520, margin: "48px auto", padding: "48px 24px",
            background: "#fff", borderRadius: 22,
            boxShadow: "0 4px 34px rgba(0,0,0,0.08)",
          }}
        >
          <h2 style={{ fontSize: 21, fontWeight: 800, color: "#1e293b", margin: 0 }}>
            קופה וחשבוניות זמינות אחרי חיבור מורנינג
          </h2>
          <p style={{ fontSize: 14.5, color: "#64748b", margin: 0, lineHeight: 1.7 }}>
            כדי להפיק חשבוניות מהמערכת צריך לחבר קודם את חשבון המורנינג (Morning) שלכם
            בהגדרות התשלומים. אחרי החיבור המסך ייפתח אוטומטית, והמסמכים יופקו ויסונכרנו
            מול הספק שלכם.
          </p>
          <button
            type="button"
            onClick={() => navigate(paths.paymentSettings)}
            style={{
              marginTop: 6, padding: "12px 30px", border: "none", borderRadius: 10,
              background: "#DE1468", color: "#fff", fontSize: 15.5, fontWeight: 700,
              cursor: "pointer",
            }}
          >
            מעבר להגדרות תשלומים
          </button>
        </div>
      </Layout>
    );
  }

  const playCog = () => {
    // Restart from frame 0 on hover/click — gives a snappy little spin every time
    try { cogRef.current?.goToAndPlay?.(0, true); } catch (e) { /* noop */ }
  };

  return (
    <Layout>
      <div style={{ direction: "rtl", padding: 0 }}>
        <div
          style={{
            display: "flex",
            gap: 8,
            borderBottom: "1px solid #E5E7EB",
            padding: "12px 24px",
            background: "#fff",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => { setTab(t.id); if (t.icon === "cog") playCog(); }}
                onMouseEnter={() => { if (t.icon === "cog") playCog(); }}
                style={{
                  background: active ? "#E93D67" : "transparent",
                  color: active ? "#fff" : "#374151",
                  border: active ? "none" : "1px solid #E5E7EB",
                  borderRadius: 8,
                  padding: "8px 16px",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                }}
              >
                {t.icon === "cog" && (
                  <span style={{ width: 22, height: 22, display: "inline-flex", filter: active ? "brightness(0) invert(1)" : "none" }}>
                    <Lottie
                      lottieRef={cogRef}
                      animationData={settingsCogLottie}
                      loop={false}
                      autoplay={false}
                      style={{ width: 22, height: 22 }}
                    />
                  </span>
                )}
                {t.label}
              </button>
            );
          })}
        </div>
        {tab === "pos" && <PosInvoicing onIssued={() => setTab("list")} />}
        {tab === "list" && <DocumentsList onNewDoc={() => setTab("pos")} />}
        {tab === "debts" && <DocumentsDebts />}
        {tab === "settings" && <DocumentsSettings />}
      </div>
    </Layout>
  );
};

export default Documents;
